import { useEffect, useMemo, useState } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    styled,
    Typography,
    useTheme,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { parsePhoneNumber } from 'libphonenumber-js';
import * as Yup from 'yup';

import { navigate } from '@redwoodjs/router';
import { MetaTags, useMutation, useQuery } from '@redwoodjs/web';

// Reusable Components
import { PhoneNumberField } from 'src/components/FormikComponents';
import Loader from 'src/components/Loader/Loader';
import ModalContent from 'src/components/ModalContent/ModalContent';
import { SearchInput } from 'src/components/SearchInput';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { DEFAULT_BORDER, Hierarchy } from 'src/constants';
import { useAuthContext } from 'src/contexts/AuthProvider';
// Mutations & Queries
import { DELETE_USER_MUTATION } from 'src/graphql/mutations/deleteUser.mutation';
import { UPDATE_USER_MUTATION } from 'src/graphql/mutations/updateUser.mutation';
import { GET_ROLES } from 'src/graphql/queries/roles.query';
// Types
import { GET_USER } from 'src/graphql/queries/users.query';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import { formatToInternational } from 'src/lib/formatters';
import { UserObject } from 'src/types/edit-user';

// Components
import { useEmailAddressValidation } from 'src/utils/validateEmail';

import UserConfig from './UserConfig';
import UserDetailsCard from './UserDetailsCard';
import UserStatus from './UserStatus';
import UserTypes from './UserTypes';

const UserDetailsForm = styled('form')({
    width: '100%',
});

type EditUserValues = UserObject & {
    role: string;
    isWorldPaySeller: boolean;
    sellerId: string;
    isPipeDriveUser: boolean;
    pipeDriveUserId: string;
    hasTechnicalSupportAccess: boolean;
    zenDeskUserId: string;
    mobileNumber: string;
};

type ViewUserProps = {
    id: number;
};

const editUserValidation = Yup.object().shape({
    mobileNumber: Yup.number().required('Required'),
    email: Yup.string().required('Required').email('Please enter a valid email address'),
});

const EditUserPage = ({ id }: ViewUserProps) => {
    const theme = useTheme();
    const [userData, setUserData] = useState();
    const { user, setUser } = useAuthContext();

    const [userRolesByHierarchy, setUserRolesByHierarchy] = useState([]);
    const [emailIsValid, setEmailIsValid] = useState<boolean>(true);

    // Query used to fetch a user's data by id
    const { loading: loadingUser, refetch } = useQuery(GET_USER, {
        variables: { id },
        onCompleted: (data) => {
            setUserData(data.user);
        },
        onError: (error) => {
            showErrorToast(error.message);
        },
    });
    // Function to close forms
    const closeForms = () => {
        setEditUserDetails(false);
        setEditUserConfig(false);
        refetch();
    };

    // Mutation used to update a user record on the database
    const [updateUser, { loading: updatingUser }] = useMutation(UPDATE_USER_MUTATION, {
        onCompleted: (data) => {
            if (data?.updateUser?.id === user?.id) {
                // Update session data for currentUser
                setUser({
                    ...user,
                    ...data?.updateUser,
                });
            }
            showSuccessToast('User Updated Successfully');
            closeForms();
        },
        onError: (error) => {
            showErrorToast(error.message ?? 'Error Updating the Users Info');
        },
    });

    // Mutation used to delete a user record from the database
    const [deleteUser, { loading: deletingUser }] = useMutation(DELETE_USER_MUTATION, {
        onCompleted: () => {
            showSuccessToast('User Deleted Successfully');
            navigate(`/user-management/users`);
        },
        onError: (error) => {
            showErrorToast(error.message ?? 'Error Deleting User');
        },
    });

    // Query used to fetch list of user roles for roles dropDown
    const { data: userRoles } = useQuery(GET_ROLES, {
        onError: (error) => {
            showErrorToast(error.message);
        },
    });

    const { roles } = userRoles ?? [];

    // Track the open state of the edit user details form
    const [editUserDetails, setEditUserDetails] = useState(false);

    const [editUserConfig, setEditUserConfig] = useState(false);

    // Function to toggle the open/close state of the edit user form
    const toggleOpenUserDetailsForm = () => {
        setEditUserDetails((prev) => !prev);
    };
    // Function to toggle the open/close state of the edit user form
    const toggleOpenConfigForm = () => {
        setEditUserConfig((prev) => !prev);
    };
    // Track the open/close state of the delete modal
    const [openDeleteModal, setOpenDeleteModal] = useState(false);

    // Function used to open the delete modal
    const handleOpenDeleteModal = () => {
        setOpenDeleteModal(true);
    };

    // Function used to close the delete modal
    const handleCloseDeleteModal = () => {
        setOpenDeleteModal(false);
    };

    // Function used to delete a user from the database
    const handleDeleteRecord = () => {
        deleteUser({ variables: { id: userData?.id } });
        handleCloseDeleteModal();
    };

    const handleRedirectToUsersPage = () => {
        navigate(`/user-management/users`);
    };
    const { isEmailAddressValid } = useEmailAddressValidation();
    const handleValidateEmail = async (email: string) => {
        const isEmailAValid = await isEmailAddressValid(email);
        if (isEmailAValid) {
            showSuccessToast('Email is valid');
            setEmailIsValid(true);
        } else {
            showErrorToast('Email is not valid');
            setEmailIsValid(false);
        }
    };

    const createCountryCode = (phoneNumber: string) => {
        try {
            if (phoneNumber) {
                const parsedNumber = parsePhoneNumber(phoneNumber);
                const countries = parsedNumber.getPossibleCountries();
                if (countries.length) {
                    return countries[0];
                }
                return;
            }
        } catch (error) {
            // return Ireland as the country code by default
            return 'IE';
        }
    };

    const initialValues = useMemo(
        () =>
            !userData
                ? null
                : {
                      ...userData,
                      role: userData?.role?.id,
                      isWorldPaySeller: Boolean(userData?.sellerId),
                      isPipeDriveUser: Boolean(userData?.pipeDriveUserId),
                      hasTechnicalSupportAccess: Boolean(userData?.zenDeskUserId),
                      mobileNumber: userData?.phoneNumber,
                      // If for whatever reason the user has no country code, create one using their internationally formatted phone number
                      countryCode:
                          userData?.countryCode || createCountryCode(userData?.phoneNumber) || 'IE',
                  },
        [userData]
    );

    const handleFormSubmission = ({
        mobileNumber,
        email,
        role,
        countryCode,
        isWorldPaySeller,
        sellerId,
        isPipeDriveUser,
        pipeDriveUserId,
        hasTechnicalSupportAccess,
        zenDeskUserId,
    }: EditUserValues) => {
        // Update the user with the form values
        updateUser({
            variables: {
                id: userData?.id,
                input: {
                    phoneNumber: formatToInternational(mobileNumber.toString(), countryCode),
                    email,
                    roleId: parseInt(role),
                    countryCode,
                    sellerId: isWorldPaySeller ? sellerId : null,
                    pipeDriveUserId: isPipeDriveUser ? pipeDriveUserId : null,
                    zenDeskUserId: hasTechnicalSupportAccess ? zenDeskUserId : null,
                },
            },
        });
    };

    useEffect(() => {
        if (!roles) return;

        const filteredRolesByHierarchy = roles.filter((role) => {
            let hierarchyLevel = role.hierarchyLevel;
            if (hierarchyLevel === Hierarchy.PARTNER) {
                hierarchyLevel = Hierarchy.PARTNER_OF_PARTNER;
                return hierarchyLevel === userData?.role?.hierarchyLevel.toUpperCase();
            } else {
                return hierarchyLevel === userData?.role?.hierarchyLevel.toUpperCase();
            }
        });

        setUserRolesByHierarchy(filteredRolesByHierarchy);
    }, [roles, userData]);

    if (loadingUser && !userData) return <Loader />;

    return (
        <PageContent
            heading="View User"
            subHeading="View, Edit or Remove this user"
            callToActions={
                <>
                    {userData?.status?.length > 0 ? (
                        <>
                            <UserStatus
                                status={userData?.status}
                                userId={userData?.id}
                                userName={`${userData?.firstName} ${userData?.lastName}`}
                            />
                            <Button
                                startIcon={<DeleteIcon />}
                                variant="npi-primary"
                                onClick={handleOpenDeleteModal}
                            >
                                Remove User
                            </Button>
                        </>
                    ) : null}
                </>
            }
        >
            <MetaTags title="EditUser page" description="EditUser page" />
            {(updatingUser || deletingUser) && <Loader />}
            <Grid container gap={3}>
                <Formik
                    initialValues={initialValues}
                    onSubmit={handleFormSubmission}
                    validationSchema={editUserValidation}
                    enableReinitialize
                >
                    {({
                        values,
                        errors,
                        touched,
                        handleSubmit,
                        handleChange,
                        setFieldValue,
                        setFieldError,
                        handleBlur,
                        isValid,
                    }) => {
                        return (
                            <Form
                                onSubmit={handleSubmit}
                                style={{
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '24px',
                                }}
                            >
                                <UserTypes
                                    hierarchyLevel={userData?.role?.hierarchyLevel}
                                    // TO-DO: fetch correct data once Business table is connected to the User table
                                    partner={'N/A'}
                                    partnerOfPartner={'N/A'}
                                    // partner={userObject.partner}
                                    // partnerOfPartner={userObject.partnerOfPartner ?? 'N/A'}
                                />

                                {!editUserDetails ? (
                                    <UserDetailsCard
                                        firstName={userData?.firstName}
                                        lastName={userData?.lastName}
                                        role={userData?.role?.name}
                                        email={userData?.email}
                                        phoneNumber={userData?.phoneNumber}
                                        username={userData?.username}
                                        openUserDetailsForm={toggleOpenUserDetailsForm}
                                    />
                                ) : (
                                    <UserDetailsForm
                                        onSubmit={handleSubmit}
                                        noValidate
                                        autoComplete="off"
                                    >
                                        <Box display="flex" flexDirection="column" gap={3}>
                                            <Grid
                                                item
                                                xs={12}
                                                sx={{
                                                    ...DEFAULT_BORDER,
                                                    backgroundColor: '#FFFFFF',
                                                    boxShadow: '0px 4px 20px rgba(38, 50, 56, 0.1)',
                                                    padding: '32px 24px',
                                                }}
                                                gap={2}
                                                container
                                            >
                                                <Grid item xs={12} gap={3} container>
                                                    <Grid
                                                        item
                                                        xs={12}
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                        sx={{ borderBottom: '1px solid #F18F34' }}
                                                    >
                                                        <Box
                                                            display="flex"
                                                            flexDirection="column"
                                                            gap="8px"
                                                            marginBottom="8px"
                                                        >
                                                            <Typography variant="h2">
                                                                User Details
                                                            </Typography>
                                                            <Typography>
                                                                Add new user details in this section
                                                            </Typography>
                                                        </Box>
                                                        <IconButton
                                                            sx={{
                                                                color: 'black',
                                                                '&:hover': { background: 'none' },
                                                            }}
                                                            onClick={toggleOpenUserDetailsForm}
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Grid>
                                                    <Grid item xs={12} spacing={2} container>
                                                        <>
                                                            {/* First Name */}
                                                            <Grid
                                                                item
                                                                xs={12}
                                                                lg={6}
                                                                display="flex"
                                                                flexDirection="column"
                                                                gap={1}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: '14px',
                                                                        fontWeight: 600,
                                                                    }}
                                                                    color={theme.palette.dark.main}
                                                                >
                                                                    First Name
                                                                </Typography>

                                                                <Typography
                                                                    sx={{
                                                                        fontSize: '20px',
                                                                        fontWeight: 400,
                                                                    }}
                                                                >
                                                                    {userData?.firstName}
                                                                </Typography>
                                                            </Grid>
                                                            {/* Last Name */}
                                                            <Grid
                                                                item
                                                                xs={12}
                                                                lg={6}
                                                                display="flex"
                                                                flexDirection="column"
                                                                gap={1}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: '14px',
                                                                        fontWeight: 600,
                                                                    }}
                                                                    color={theme.palette.dark.main}
                                                                >
                                                                    Last Name
                                                                </Typography>

                                                                <Typography
                                                                    sx={{
                                                                        fontSize: '20px',
                                                                        fontWeight: 400,
                                                                    }}
                                                                >
                                                                    {userData?.lastName}
                                                                </Typography>
                                                            </Grid>
                                                            {/* Mobile number */}
                                                            <Grid item xs={12} lg={6}>
                                                                <PhoneNumberField
                                                                    setFieldError={setFieldError}
                                                                    setFieldValue={setFieldValue}
                                                                    formikFieldName={'mobileNumber'}
                                                                    initialPhoneNumber={
                                                                        userData?.phoneNumber ?? ''
                                                                    }
                                                                />
                                                            </Grid>
                                                            {/* Email */}
                                                            <Grid item xs={12} lg={6}>
                                                                <SearchInput
                                                                    name="email"
                                                                    label="Email Address"
                                                                    value={values.email}
                                                                    onChange={handleChange}
                                                                    error={
                                                                        !!errors.email ||
                                                                        !emailIsValid
                                                                            ? errors.email ??
                                                                              'Email is Invalid'
                                                                            : undefined
                                                                    }
                                                                    boxStyling={{ width: '100%' }}
                                                                    searchText="Validate Email"
                                                                    onSearch={handleValidateEmail}
                                                                />
                                                            </Grid>
                                                            {/* User Role Drop Down */}
                                                            {userRolesByHierarchy.length !== 0 && (
                                                                <Grid item xs={12} lg={6}>
                                                                    <FormControl fullWidth>
                                                                        <InputLabel id="userRole">
                                                                            User Role
                                                                        </InputLabel>
                                                                        <Select
                                                                            labelId="role"
                                                                            id="role"
                                                                            name="role"
                                                                            required
                                                                            value={values.role}
                                                                            label="User Role"
                                                                            onChange={(event) => {
                                                                                setFieldValue(
                                                                                    'role',
                                                                                    event.target
                                                                                        .value
                                                                                );
                                                                            }}
                                                                            error={
                                                                                touched.role &&
                                                                                Boolean(errors.role)
                                                                            }
                                                                            onBlur={handleBlur}
                                                                        >
                                                                            {userRolesByHierarchy.map(
                                                                                (userRole) => (
                                                                                    <MenuItem
                                                                                        key={
                                                                                            userRole.id
                                                                                        }
                                                                                        value={
                                                                                            userRole.id
                                                                                        }
                                                                                    >
                                                                                        {
                                                                                            userRole.name
                                                                                        }
                                                                                    </MenuItem>
                                                                                )
                                                                            )}
                                                                        </Select>
                                                                        <FormHelperText>
                                                                            {touched.role &&
                                                                                errors.role}
                                                                        </FormHelperText>
                                                                    </FormControl>
                                                                </Grid>
                                                            )}

                                                            <Grid
                                                                item
                                                                xs={12}
                                                                lg={6}
                                                                display="flex"
                                                                flexDirection="column"
                                                                gap={1}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: '14px',
                                                                        fontWeight: 600,
                                                                    }}
                                                                    color={theme.palette.dark.main}
                                                                >
                                                                    Username
                                                                </Typography>

                                                                <Typography
                                                                    sx={{
                                                                        fontSize: '20px',
                                                                        fontWeight: 400,
                                                                    }}
                                                                >
                                                                    {userData?.username}
                                                                </Typography>
                                                            </Grid>
                                                        </>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </UserDetailsForm>
                                )}

                                {/* Configuration Section */}
                                <UserConfig
                                    toggleOpenConfigForm={toggleOpenConfigForm}
                                    editConfig={editUserConfig}
                                    setFieldValue={setFieldValue}
                                    handleChange={handleChange}
                                    values={values}
                                    errors={errors}
                                />

                                {/* Submit Buttons */}
                                {editUserDetails || editUserConfig ? (
                                    <Grid
                                        item
                                        xs={12}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            gap: '8px',
                                        }}
                                    >
                                        <Button
                                            variant="outlined-black"
                                            sx={{ width: '146px' }}
                                            onClick={closeForms}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="main"
                                            sx={{ width: '146px' }}
                                            type="submit"
                                            disabled={!isValid}
                                        >
                                            Update User
                                        </Button>
                                    </Grid>
                                ) : (
                                    <Grid
                                        item
                                        xs={12}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            gap: '8px',
                                        }}
                                    >
                                        <Button
                                            variant="outlined-black"
                                            sx={{ width: '146px' }}
                                            onClick={handleRedirectToUsersPage}
                                        >
                                            Back
                                        </Button>
                                    </Grid>
                                )}
                            </Form>
                        );
                    }}
                </Formik>
            </Grid>
            <Dialog
                open={openDeleteModal}
                onClose={handleCloseDeleteModal}
                aria-labelledby="delete-user-dialog-title"
                sx={{
                    '& .MuiDialog-paper': {
                        width: '400px',
                    },
                }}
            >
                <DialogContent>
                    <ModalContent
                        handleClose={handleCloseDeleteModal}
                        handleAction={handleDeleteRecord}
                        header="Delete User?"
                        message="Are you sure you want to permanently delete this user? The user cannot be restored once deleted"
                        buttonText="Delete"
                    />
                </DialogContent>
            </Dialog>
        </PageContent>
    );
};

export default EditUserPage;
